/**
 * PDF Viewer Service
 *
 * Utilities for loading and normalizing PDF sources for the PDF viewer component.
 */

import { Platform } from 'react-native';
import { PDFSource } from '../types/pdf';
import { attachmentService } from './attachmentService';

/**
 * Convert a Blob to a Base64 string
 */
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      // Extract just the base64 data without the data URL prefix
      const base64 = dataUrl.split(',')[1];
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read blob as base64'));
    reader.readAsDataURL(blob);
  });
};

/**
 * Normalize a PDF source to a format the viewer can use
 *
 * Different platforms and PDF libraries accept different formats:
 * - Web (react-pdf): Accepts data URLs, URLs, or Blob URLs
 * - Native (react-native-pdf): Accepts file URIs or data URLs
 *
 * This function normalizes all input types to a data URL format.
 *
 * @param source - The PDF source to normalize
 * @returns A data URL string that can be used by the PDF viewer
 */
export const normalizePDFSource = async (source: PDFSource): Promise<string> => {
  const mimeType = source.mimeType || 'application/pdf';

  switch (source.type) {
    case 'base64':
      // Convert raw base64 to data URL
      return `data:${mimeType};base64,${source.data}`;

    case 'dataUrl':
      // Already a data URL, return as-is
      return source.data as string;

    case 'url':
      // Remote URL - return as-is for web, fetch and convert for native
      if (Platform.OS === 'web') {
        return source.data as string;
      }
      // For native, we need to fetch the PDF and convert to data URL
      // react-native-pdf can handle URLs directly, so return as-is
      return source.data as string;

    case 'blob':
      // Convert Blob to data URL
      if (Platform.OS === 'web') {
        // On web, we can use URL.createObjectURL for better performance
        return URL.createObjectURL(source.data as Blob);
      }
      // For native, convert blob to base64
      const base64 = await blobToBase64(source.data as Blob);
      return `data:${mimeType};base64,${base64}`;

    default:
      throw new Error(`Invalid PDF source type: ${(source as PDFSource).type}`);
  }
};

/**
 * Load a PDF from an attachment ID
 *
 * Fetches the attachment data from the server and creates a PDFSource.
 *
 * @param attachmentId - The ID of the attachment to load
 * @returns A PDFSource ready for the viewer
 * @throws Error if the attachment is not found or is not a PDF
 */
export const loadPDFFromAttachment = async (attachmentId: number): Promise<PDFSource> => {
  const result = await attachmentService.getAttachment(attachmentId);

  if (!result.success || !result.attachment) {
    throw new Error(result.error || 'Failed to load attachment');
  }

  const { attachmentFile, mimeType, attachmentName } = result.attachment;

  if (!attachmentFile) {
    throw new Error('Attachment has no file data');
  }

  if (mimeType !== 'application/pdf') {
    throw new Error(`Attachment is not a PDF (type: ${mimeType})`);
  }

  return {
    type: 'base64',
    data: attachmentFile,
    mimeType: 'application/pdf',
    fileName: attachmentName,
  };
};

/**
 * Create a PDFSource from a Blob (typically from PDF generation)
 *
 * @param blob - The PDF Blob
 * @param fileName - Optional filename for display/download
 * @returns A PDFSource ready for the viewer
 */
export const loadPDFFromBlob = (blob: Blob, fileName?: string): PDFSource => {
  return {
    type: 'blob',
    data: blob,
    mimeType: 'application/pdf',
    fileName: fileName || 'document.pdf',
  };
};

/**
 * Create a PDFSource from a data URL (typically from pdfService)
 *
 * @param dataUrl - The PDF as a data URL (e.g., from generateFilledPDF on native)
 * @param fileName - Optional filename for display/download
 * @returns A PDFSource ready for the viewer
 */
export const loadPDFFromDataUrl = (dataUrl: string, fileName?: string): PDFSource => {
  return {
    type: 'dataUrl',
    data: dataUrl,
    mimeType: 'application/pdf',
    fileName: fileName || 'document.pdf',
  };
};

/**
 * Create a PDFSource from a remote URL
 *
 * @param url - The URL to the PDF file
 * @param fileName - Optional filename for display/download
 * @returns A PDFSource ready for the viewer
 */
export const loadPDFFromUrl = (url: string, fileName?: string): PDFSource => {
  // Extract filename from URL if not provided
  const extractedFileName = fileName || url.split('/').pop()?.split('?')[0] || 'document.pdf';

  return {
    type: 'url',
    data: url,
    mimeType: 'application/pdf',
    fileName: extractedFileName,
  };
};

/**
 * Check if a MIME type represents a PDF
 */
export const isPDFMimeType = (mimeType?: string): boolean => {
  return mimeType === 'application/pdf';
};

export const pdfViewerService = {
  normalizePDFSource,
  loadPDFFromAttachment,
  loadPDFFromBlob,
  loadPDFFromDataUrl,
  loadPDFFromUrl,
  isPDFMimeType,
};
